import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { classNames } from '@/utils';
import type { ReactNode } from 'react';
import {
  Bar,
  LabelList,
  BarChart as RechartsBarChart,
  XAxis,
  YAxis,
} from 'recharts';
import { DEFAULT_ANIMATION } from '../constants';
import { BarChartProps } from '../types';
import { formatValue, getChartColors } from '../utils';
import { ChartEmpty, ChartError, ChartLoading } from './ChartStates';

export function HorizontalBarChart({
  data,
  dataKeys,
  format = 'number',
  currencyCode,
  colors,
  tooltip,
  animate = DEFAULT_ANIMATION.enabled,
  loading,
  error,
  emptyState,
  fillParent = false,
  className = '',
  style,
}: Omit<
  BarChartProps,
  | 'layout'
  | 'showLabels'
  | 'stacked'
  | 'xAxis'
  | 'yAxis'
  | 'legend'
  | 'showLegend'
>) {
  // Handle loading state
  if (loading) {
    return <ChartLoading fillParent={fillParent} className={className} />;
  }

  // Handle error state
  if (error) {
    return (
      <ChartError error={error} fillParent={fillParent} className={className} />
    );
  }

  // Handle empty state
  if (!data || data.length === 0) {
    return (
      <ChartEmpty fillParent={fillParent} className={className}>
        {emptyState}
      </ChartEmpty>
    );
  }

  // Determine which keys to display
  const keys = dataKeys || ['value'];
  const primaryKey = keys[0];

  // Calculate max value for positioning labels at chart edge
  const maxValue = Math.max(
    ...data.map((item) => Number(item[primaryKey]) || 0),
  );

  // Add 10% padding to domain for label space
  const domainPadding = 1.1;
  const domainMax = maxValue * domainPadding;

  // Get colors for bars
  const barColors = colors || getChartColors(keys.length);

  // Build chart config
  const chartConfig: ChartConfig = keys.reduce((config, key, index) => {
    config[key] = {
      label: key.charAt(0).toUpperCase() + key.slice(1),
      color: barColors[index],
    };
    return config;
  }, {} as ChartConfig);

  // Custom formatter for tooltip
  const tooltipFormatter =
    tooltip?.formatter ||
    ((value: number, _, item) => {
      return (
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-muted-foreground">{item.payload.label}</span>
          <span className="text-foreground font-mono font-medium tabular-nums">
            {formatValue(value, format, currencyCode)}
          </span>
        </div>
      );
    });

  const containerClass = classNames(
    fillParent ? 'absolute inset-0' : 'w-full min-h-64',
    className,
  );

  // Calculate dynamic height based on number of items (64px per bar row for label + bar + spacing)
  const chartHeight = data.length * 64;

  return (
    <div className={containerClass} style={style}>
      <ChartContainer config={chartConfig} className="h-full w-full">
        <RechartsBarChart
          data={data}
          layout="vertical"
          margin={{ top: 20, right: 0, bottom: 0, left: 0 }}
          barSize={6}
          height={chartHeight}
        >
          <YAxis
            dataKey="label"
            type="category"
            tickLine={false}
            axisLine={false}
            width={0}
            hide
          />
          <XAxis type="number" hide domain={[0, domainMax]} />
          {tooltip?.enabled !== false && (
            <ChartTooltip
              cursor={false}
              content={
                typeof tooltip?.content === 'function' ? (
                  tooltip.content
                ) : tooltip?.content ? (
                  () => tooltip.content as ReactNode
                ) : (
                  <ChartTooltipContent
                    indicator="line"
                    formatter={
                      tooltipFormatter as Parameters<
                        typeof ChartTooltipContent
                      >[0]['formatter']
                    }
                  />
                )
              }
            />
          )}
          {keys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={barColors[index]}
              radius={[3, 3, 3, 3]}
              background={{ radius: 3 }}
              isAnimationActive={animate}
              animationDuration={animate ? DEFAULT_ANIMATION.duration : 0}
            >
              <LabelList
                dataKey="label"
                position="insideLeft"
                className="fill-foreground font-inter"
                fontSize={12}
                letterSpacing={'-1%'}
                fontWeight={500}
                content={(props) => {
                  const { x, y, value } = props as {
                    x: number;
                    y: number;
                    value: string;
                  };
                  return (
                    <text
                      x={x}
                      y={y - 8}
                      className="fill-foreground font-inter text-base-900 text-xs font-medium tracking-tight"
                      textAnchor="start"
                    >
                      {value}
                    </text>
                  );
                }}
              />
              <LabelList
                dataKey={key}
                position="right"
                formatter={(value: number) =>
                  formatValue(value, format, currencyCode)
                }
                className="fill-foreground font-inter text-base-900 text-xs font-medium tracking-tight"
                fontSize={12}
                letterSpacing={'-1%'}
                fontWeight={500}
                content={(props) => {
                  const { x, y, value, width } = props as {
                    x: number;
                    y: number;
                    value: number;
                    width: number;
                  };
                  // Calculate full chart width: if this bar has `width` pixels for `value`,
                  // then the full chart width (for domainMax) = width * (domainMax / value)
                  const chartEndX =
                    value > 0 ? x + width * (domainMax / value) : x + width;
                  return (
                    <text
                      x={chartEndX}
                      y={y - 8}
                      className="fill-foreground font-inter text-base-900 text-xs font-medium tracking-tight"
                      textAnchor="end"
                    >
                      {formatValue(value, format, currencyCode)}
                    </text>
                  );
                }}
              />
            </Bar>
          ))}
        </RechartsBarChart>
      </ChartContainer>
    </div>
  );
}
